/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement, removeClassNamesFromElement } from "@lexical/utils";
import { isContractValidationPage, isSetupPage } from "helpers/utils";
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from "lexical";
import { ElementNode } from "lexical";

import { recalculateNumberingArticles } from ".";

type SerializedArticleContainerNode = Spread<
    {
        open: boolean;
        index: number[];
        referenceKey: string;
        show: boolean;
        conditional: boolean;
    },
    SerializedElementNode
>;

export function convertDetailsElement(domNode: HTMLDetailsElement): DOMConversionOutput | null {
    const isOpen = domNode.open !== undefined ? domNode.open : true;
    // const index = domNode.index !== undefined ? domNode.index : 0;
    const node = $createArticleContainerNode(isOpen, [1], "", true, false);
    return {
        node,
    };
}

export class ArticleContainerNode extends ElementNode {
    /**
     * @param {boolean} __open Used for the details element to determine if it is open or not, but not used anymore since we don't want to collapse the article container now
     */
    __open: boolean;
    /**
     * @param {number[]} __index Stores the index of the article container
     */
    __index: number[];
    /**
     * @param {string} __referenceKey Stores the reference key of the article container, gets created when a reference is created
     */
    __referenceKey: string;
    /**
     * @param {boolean} __show The show property is used to determine if the article container should be shown or not on the setup page and agreement validation page
     */
    __show: boolean = true;
    /**
     * @param {boolean} __conditional The conditional property is used to determine if the article container is conditional or not
     */
    __conditional: boolean = false;

    constructor(
        open: boolean,
        index: number[],
        referenceKey: string,
        show: boolean,
        conditional: boolean,
        key?: NodeKey,
    ) {
        super(key);
        this.__open = open;
        this.__index = index;
        this.__referenceKey = referenceKey;
        this.__show = show;
        this.__conditional = conditional;
    }

    static getType(): string {
        return "article-container";
    }

    getIndex(): number[] {
        const self = this.getLatest();
        return self.__index;
    }

    setIndex(value: number[]) {
        const self = this.getWritable();
        self.__index = value;
    }

    setIndexAt(value: number, at: number) {
        const self = this.getWritable();
        self.__index[at] = value;
    }

    getReferenceKey(): string {
        const self = this.getLatest();
        return self.__referenceKey;
    }

    getPrevReferenceKey(): string {
        return this.__referenceKey;
    }

    setReferenceKey(value: string) {
        const self = this.getWritable();
        self.__referenceKey = value;
    }

    getShow(): boolean {
        const self = this.getLatest();
        return self.__show;
    }

    setShow(value: boolean) {
        const self = this.getWritable();
        self.__show = value;
    }

    toggleShow(): void {
        this.setShow(!this.getShow());
    }

    getConditional(): boolean {
        const self = this.getLatest();
        return self.__conditional;
    }

    setConditional(value: boolean) {
        const self = this.getWritable();
        self.__conditional = value;
    }

    toggleConditional(): void {
        this.setConditional(!this.getConditional());
    }

    static clone(node: ArticleContainerNode): ArticleContainerNode {
        return new ArticleContainerNode(
            node.__open,
            node.__index,
            node.__referenceKey,
            node.__show,
            node.__conditional,
            node.__key,
        );
    }

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
        const dom = document.createElement("details");
        addClassNamesToElement(dom, config.theme.articleContainer);
        dom.open = true;
        if (this.__conditional && !isContractValidationPage()) addClassNamesToElement(dom, "conditional");
        addClassNamesToElement(dom, "show");
        // the checkbox is used for change the __show property on setup
        const checkbox = dom.appendChild(document.createElement("input"));
        checkbox.type = "checkbox";
        checkbox.style.display = "none";
        if (isSetupPage() && this.__conditional) {
            checkbox.style.display = "block";
            checkbox.checked = this.getShow();
            addClassNamesToElement(checkbox, config.theme.conditionalCheckbox);
            checkbox.addEventListener("change", (e) => {
                const show = editor.getEditorState().read(() => this.getShow());
                if (show !== checkbox.checked) {
                    editor.update(() => {
                        this.toggleShow();
                        recalculateNumberingArticles(true, true);
                    });
                }
            });
            if (this.__show) addClassNamesToElement(dom, "show");
            else removeClassNamesFromElement(dom, "show");
        }
        return dom;
    }

    updateDOM(prevNode: ArticleContainerNode, dom: HTMLDetailsElement): boolean {
        if (prevNode.__open !== this.__open) {
            dom.open = this.__open;
        }
        if (prevNode.__show !== this.__show) {
            (dom.firstChild as HTMLInputElement).checked = this.__show;
            // dom.classList.toggle("show", this.__show);
            if (this.__show) addClassNamesToElement(dom, "show");
            else removeClassNamesFromElement(dom, "show");
        }

        if (this.__show) addClassNamesToElement(dom, "show");
        else removeClassNamesFromElement(dom, "show");

        if (this.__conditional && !isContractValidationPage()) addClassNamesToElement(dom, "conditional");
        else removeClassNamesFromElement(dom, "conditional");

        const title = dom.childNodes as NodeListOf<HTMLElement> | null;
        if (title) {
            addClassNamesToElement(title[1], `depth-${this.__index.length}`);
        }

        return false;
    }

    static importDOM(): DOMConversionMap<HTMLDetailsElement> | null {
        return {
            details: (domNode: HTMLDetailsElement) => {
                return {
                    conversion: convertDetailsElement,
                    priority: 1,
                };
            },
        };
    }

    static importJSON(serializedNode: SerializedArticleContainerNode): ArticleContainerNode {
        const node = $createArticleContainerNode(
            serializedNode.open,
            serializedNode.index,
            serializedNode.referenceKey,
            serializedNode.show,
            serializedNode.conditional,
        );
        return node;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("details");
        addClassNamesToElement(element, "Article__container");
        element.setAttribute("open", this.__open.toString());
        if (this.__show) addClassNamesToElement(element, "show");
        if (!this.__show) return { element: null };
        return { element };
    }

    exportJSON(): SerializedArticleContainerNode {
        return {
            ...super.exportJSON(),
            open: this.__open,
            index: this.__index,
            referenceKey: this.__referenceKey,
            show: this.__show,
            conditional: this.__conditional,
            type: "article-container",
            version: 1,
        };
    }

    setOpen(open: boolean): void {
        const writable = this.getWritable();
        writable.__open = open;
    }

    getOpen(): boolean {
        return this.getLatest().__open;
    }

    toggleOpen(): void {
        this.setOpen(!this.getOpen());
    }
}

export function $createArticleContainerNode(
    isOpen: boolean,
    index: number[],
    referenceKey: string,
    show: boolean,
    conditional: boolean,
): ArticleContainerNode {
    return new ArticleContainerNode(isOpen, index, referenceKey, show, conditional);
}

export function $isArticleContainerNode(node: LexicalNode | null | undefined): node is ArticleContainerNode {
    return node instanceof ArticleContainerNode;
}
